/**
 * HTML Sanitization Utility
 * Sanitizes HTML content to prevent XSS attacks using DOMPurify
 * 
 * Requires: js/vendor/purify.min.js to be loaded in Index.aspx
 * 
 * Usage:
 *   import { sanitizeHtml, sanitizeForDisplay } from './utils/htmlSanitizer';
 *   const safe = sanitizeHtml(unsafeHtml);
 *   const display = sanitizeForDisplay(unsafeHtml); // also cleans formula garbage
 */

/**
 * Main sanitization function using DOMPurify
 * DOMPurify must be loaded from js/vendor/purify.min.js
 */
function stripBrokenFormulaImages(html) {
  if (!html || typeof html !== 'string') return html;
  html = html.replace(/<img\b[^>]*\bsrc\s*=\s*["'][^"']*\?q=[^"']*["'][^>]*\/?>/gi, '');
  html = html.replace(/<img\b[^>]*\balt\s*=\s*["'][^"']*(?:%3Cflashrichtext|flashrichtext)[^"']*["'][^>]*\/?>/gi, '');
  return html;
}

export function sanitizeHtml(unsafeHtml, options = {}) {
  if (!unsafeHtml || typeof unsafeHtml !== 'string') {
    return '';
  }

  unsafeHtml = stripBrokenFormulaImages(unsafeHtml);

  if (typeof window === 'undefined' || !window.DOMPurify) {
    console.error('DOMPurify is not loaded. Please ensure js/vendor/purify.min.js is loaded in Index.aspx');
    return '';
  }

  try {
    const config = {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'b', 'i', 'small', 'sub', 'sup',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'dl', 'dt', 'dd',
        'div', 'span', 'blockquote', 'pre', 'code',
        'a', 'img',
        'table', 'thead', 'tbody', 'tr', 'td', 'th',
        'hr'
      ],
      ALLOWED_ATTR: [
        'class', 'id', 'dir', 'lang', 'role',
        'href', 'target', 'title', 'rel',
        'src', 'srcset', 'alt', 'width', 'height',
        'colspan', 'rowspan',
        'contenteditable',
        'data-tex',
        'data-alt'
      ],
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: true,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      SAFE_FOR_TEMPLATES: false,
      ...options
    };

    return window.DOMPurify.sanitize(unsafeHtml, config);
  } catch (error) {
    console.error('DOMPurify sanitization failed:', error);
    return '';
  }
}

/**
 * Sanitize + clean up formula display artifacts.
 * Strips data-alt, replaces .mathformula innerHTML with data-tex,
 * and removes broken legacy <img> formula tags (old LaTeX server images
 * whose alt text is URL-encoded FlashRichText XML).
 * Use this for all read-only / display rendering (listings, previews, reviews).
 */
export function sanitizeForDisplay(unsafeHtml) {
  const clean = sanitizeHtml(unsafeHtml, {
    ALLOWED_ATTR: [
      'class', 'id', 'dir', 'lang', 'role',
      'href', 'target', 'title', 'rel',
      'src', 'srcset', 'alt', 'width', 'height',
      'colspan', 'rowspan',
      'contenteditable',
      'data-tex'
    ]
  });
  if (!clean) return '';

  try {
    const div = document.createElement('div');
    div.innerHTML = clean;

    // .mathformula spans: replace innerHTML with data-tex
    div.querySelectorAll('.mathformula').forEach(el => {
      const tex = el.getAttribute('data-tex');
      if (tex) el.innerHTML = tex;
    });

    // Legacy <img> with broken formula URL or garbage alt
    div.querySelectorAll('img').forEach(img => {
      const src = (img.getAttribute('src') || '').toLowerCase();
      const alt = img.getAttribute('alt') || '';
      const dataTex = img.getAttribute('data-tex') || '';

      const isBroken = (
        src.indexOf('?q=') !== -1 ||
        alt.indexOf('%3C') !== -1 ||
        alt.indexOf('%3c') !== -1 ||
        alt.toLowerCase().indexOf('flashrichtext') !== -1
      );
      if (!isBroken) return;

      if (dataTex) {
        const span = document.createElement('span');
        span.className = 'mathformula';
        span.setAttribute('contenteditable', 'false');
        span.setAttribute('data-tex', dataTex);
        span.innerHTML = dataTex;
        img.replaceWith(span);
      } else {
        img.remove();
      }
    });

    return div.innerHTML;
  } catch (error) {
    return clean;
  }
}

/**
 * Check if DOMPurify is available
 */
export function isDOMPurifyAvailable() {
  return typeof window !== 'undefined' && typeof window.DOMPurify !== 'undefined';
}
